<script>
import {
  ChartComponent,
  SeriesDirective,
  SeriesCollectionDirective,
  ColumnSeries,
  Category,
  DataLabel,
  Tooltip,
  Legend,
  Highlight,
} from '@syncfusion/ej2-vue-charts';

import { GROUP_BY_OPTIONS } from 'dashboard/routes/dashboard/settings/reports/constants.js';

import {
  messagesMetricColors,
  // COLUMN CHART RESPONSIBLE OPTIONS
  smallest_Sizes,
  smaller_1_Sizes,
  smaller_2_Sizes,
  medium_1_Sizes,
  medium_2_Sizes,
  biggerSizes,
} from '../../../../../../../../../shared/helpers/CustomDashboardsHelper.js';

export default {
  components: {
    ChartComponent,
    SeriesCollectionDirective,
    SeriesDirective,
  },
  provide: {
    chart: [ColumnSeries, Legend, DataLabel, Category, Tooltip, Highlight],
  },
  props: {
    // Current cell size
    sizeX: {
      type: Number,
      required: true,
    },
    sizeY: {
      type: Number,
      required: true,
    },
    currentWidth: {
      type: Number,
      required: true,
    },
    // If we should display the outgoing and incoming columns
    showDetailedMsgs: {
      type: Boolean,
      default: false,
    },
    groupBy: {
      type: Object,
      required: true,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      incomingColor: messagesMetricColors.incoming,
      outgoingColor: messagesMetricColors.outgoing,
      totalColor: messagesMetricColors.total,

      theme: 'Material',

      chartArea: { border: { width: 0 } },

      marker: {
        dataLabel: {
          visible: false, // Label com valor da coluna no topo da barra
          position: 'Top',
          font: { fontWeight: '600', color: '#ffffff' },
        },
      },
      legend: { enableHighlight: true },
      tooltip: {
        enable: true,
        // eslint-disable-next-line no-template-curly-in-string
        header: '<b>${point.tooltip}</b>',
        shared: true,
      },
      // title: 'GRÁFICO DE COLUNAS',
    };
  },
  computed: {
    maximumY() {
      const findMaxValue = data => {
        return data.reduce((max, entry) => {
          const maxEntryValue = Math.max(
            entry.IncomingCount || 0,
            entry.OutgoingCount || 0,
            entry.TotalCount || 0
          );
          return Math.max(max, maxEntryValue);
        }, 0);
      };
      return findMaxValue(this.seriesData) || 1;
    },
    intervalY() {
      const scales = [
        1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000,
        50000, 100000, 300000,
      ];
      return (
        scales.find(scale => this.maximumY / scale <= 4) ||
        Math.ceil(this.maximumY / 4)
      );
    },
    primaryXAxis() {
      return {
        valueType: 'Category',
        interval: 1,
        majorGridLines: { width: 0 },
        majorTickLines: { width: 0 },
        labelIntersectAction: 'Rotate45',
        labelRotation: 0,

        labelStyle: {
          size: this.responsiveSizes.xAxisLabelStyle,
          // color: 'blue',
          // fontFamily: 'Segoe UI',
          // fontWeight: 'bold',
        },
      };
    },
    // Initializing Primary Y Axis
    primaryYAxis() {
      return {
        title: this.$t('CUSTOM_DASHBOARDS.WIDGET_TYPES.MESSAGES'),
        maximum: this.maximumY,
        interval: this.intervalY,
        majorTickLines: { width: 0 },
        lineStyle: { width: 0 },
        titleStyle: {
          size: this.responsiveSizes.yAxisLabelStyle,
          fontFamily: 'var($base-font-family)',
          // textAlignment: 'center', // Alinha o texto à esquerda
        },
      };
    },
    /**
     * @returns list with dates and its options
     * Example: [{
    //       Date: 'Jul - 25',
    //       IncomingCount: 13,
    //       OutgoingCount: 27,
    //       TotalCount: 40,
    //     }, ...]
     */
    seriesData() {
      const seriesList = [];

      // Function to add the correct value for each count type
      // Since they are sent in two different objects, we need to process and merge them into a single object that the Syncfusion chart component can read correctly
      const processDataToSeries = (messages, countType) => {
        messages.forEach(e => {
          const dateAsString = this.generateDateString(e.timestamp);
          let entry = seriesList.find(s => s.Date === dateAsString);

          if (!entry) {
            entry = { Date: dateAsString };
            seriesList.push(entry);
          }

          entry[countType] = e.value;
        });
      };
      processDataToSeries(this.data.incomingMessagesReport, 'IncomingCount');
      processDataToSeries(this.data.outgoingMessagesReport, 'OutgoingCount');
      return seriesList.map(date => {
        if (!this.showDetailedMsgs)
          date.TotalCount = date.IncomingCount + date.OutgoingCount;
        return date;
      });
    },
    noMessagesFound() {
      const anyDateDifferentOfZero = this.seriesData.some(
        date =>
          date.IncomingCount > 0 ||
          date.OutgoingCount > 0 ||
          date.TotalCount > 0
      );
      return !anyDateDifferentOfZero;
    },
    groupByI18nKey() {
      const option = this.groupBy?.period.toUpperCase();
      return option ? GROUP_BY_OPTIONS[option]?.translationKey || '' : '';
    },
    responsiveSizes() {
      // FULL HD (1920x1080)
      if (window.innerWidth >= 1750) {
        if (this.sizeY > 1) {
          if (this.currentWidth < 600) return medium_1_Sizes;
          if (this.currentWidth < 1100) return medium_2_Sizes;
          return biggerSizes;
        }
        return smaller_2_Sizes;
      }
      // NOTEBOOKS (1600 X 900)
      if (window.innerWidth >= 1400) {
        if (this.sizeY > 1) {
          if (this.currentWidth < 550) return smaller_2_Sizes;
          return medium_2_Sizes;
        }
        return smaller_1_Sizes;
      }

      if (this.sizeY > 1) {
        if (this.currentWidth < 250) {
          const sizes = { ...smallest_Sizes, containerPaddingTop: '80' };
          return sizes;
        }
        if (this.currentWidth < 400) return smaller_2_Sizes;
        return medium_1_Sizes;
      }
      return smallest_Sizes;
    },
    currentLanguage() {
      let currentLanguage = this.$root.$i18n.locale.trim();
      if (currentLanguage === 'pt_BR') currentLanguage = 'pt-BR';
      else if (currentLanguage === 'es') currentLanguage = 'es-ES';
      else if (!['es', 'en'].includes(currentLanguage)) currentLanguage = 'en';
      return currentLanguage;
    },
  },
  mounted() {},
  methods: {
    // Transforming unixTimeStamp to readable time
    formatUnixToDate(unixTimestamp, rangeRepresentation = 'dayAndMonth') {
      const isMonth = rangeRepresentation === 'month';
      const isYear = rangeRepresentation === 'year';
      // Jul - 18
      if (!(isMonth || isYear)) {
        const currentMonth = this.localDateString(unixTimestamp, {
          month: 'short',
        });
        const currentDay = this.localDateString(unixTimestamp, {
          day: '2-digit',
        });
        return `${currentDay}-${currentMonth}`;
      }
      let representationParams;
      if (isMonth)
        representationParams = { month: 'short' }; // Jul
      else if (isYear) representationParams = { year: 'numeric' }; // 2024

      return this.localDateString(unixTimestamp, representationParams);
    },
    localDateString(unixTimestamp, representationParams) {
      return new Date(unixTimestamp * 1000).toLocaleDateString(
        this.currentLanguage,
        representationParams
      );
    },

    generateDateString(unixTimeStamp) {
      const sixDaysLater = timestamp => timestamp + 6 * 24 * 60 * 60;
      const { period } = this.groupBy;
      // Example: 'Jul - 14 - Jul - 20'
      if (period === 'week') {
        const from = unixTimeStamp;
        const fromAsString = this.formatUnixToDate(from);
        const endOfWeek = sixDaysLater(from);
        const endOfWeekAsString = this.formatUnixToDate(endOfWeek);
        if (this.sizeY === 1 || this.sizeX === 1 || this.currentWidth < 600)
          this.primaryXAxis.labelStyle.size = '9px';
        return `${fromAsString} - ${endOfWeekAsString}`;
      }
      // Example: 'Jul'
      if (period === 'month')
        return this.formatUnixToDate(unixTimeStamp, 'month');
      // Example: '2024'
      if (period === 'year')
        return this.formatUnixToDate(unixTimeStamp, 'year');
      // Example: 'Jul - 14'
      return this.formatUnixToDate(unixTimeStamp);
    },
    generateUniqId() {
      return parseInt(Date.now() * Math.random().toFixed(5), 10);
    },
  },
};
</script>

<template>
  <div v-if="noMessagesFound" class="text-xs">
    {{ $t('CUSTOM_DASHBOARDS.MESSAGES.NO_MESSAGES') }}
  </div>
  <div
    v-else
    class="control-section pt-25"
    :style="{ 'padding-top': responsiveSizes.containerPaddingTop }"
  >
    <div align="center">
      <div v-if="groupByI18nKey" class="text-sm text-slate-800">
        {{
          $t(
            'CUSTOM_DASHBOARDS.MESSAGES.DISPLAYS.COLUMN_CHART.DATA_GROUPED_BY'
          ) + $t(groupByI18nKey)
        }}
      </div>
      <ChartComponent
        :id="`chartcontainer-${generateUniqId()}`"
        class="block-display"
        :theme="theme"
        align="center"
        title=""
        :primary-x-axis="primaryXAxis"
        :primary-y-axis="primaryYAxis"
        :chart-area="chartArea"
        :width="responsiveSizes.containerWidth"
        :height="responsiveSizes.containerHeight"
        :tooltip="tooltip"
        :legend-settings="legend"
      >
        <SeriesCollectionDirective>
          <SeriesDirective
            v-if="!showDetailedMsgs"
            :fill="totalColor"
            :data-source="seriesData"
            type="Column"
            tooltip-mapping-name="Date"
            x-name="Date"
            y-name="TotalCount"
            name="Total"
            width="2"
            column-width="1.0"
            :marker="marker"
            column-spacing="0.1"
            legend-shape="Circle"
          />
          <SeriesDirective
            v-if="showDetailedMsgs"
            :fill="outgoingColor"
            :data-source="seriesData"
            type="Column"
            tooltip-mapping-name="Date"
            x-name="Date"
            y-name="IncomingCount"
            :name="$t('CUSTOM_DASHBOARDS.MESSAGES.INCOMING_MESSAGES')"
            width="2"
            column-width="0.6"
            :marker="marker"
            column-spacing="0.0"
            legend-shape="Circle"
          />
          <SeriesDirective
            v-if="showDetailedMsgs"
            :fill="incomingColor"
            :data-source="seriesData"
            type="Column"
            tooltip-mapping-name="Date"
            x-name="Date"
            y-name="OutgoingCount"
            :name="$t('CUSTOM_DASHBOARDS.MESSAGES.OUTGOING_MESSAGES')"
            width="2"
            column-width="0.6"
            :marker="marker"
            column-spacing="0.0"
            legend-shape="Circle"
          />
        </SeriesCollectionDirective>
      </ChartComponent>
    </div>
  </div>
</template>

<style>
.block-display {
  display: block;
}
</style>
